<script setup lang="tsx">
type EnumData = App.Base.Data.EnumData;

import type { CustomCells, HybridlyRow } from '@/components/table/Table';
import type { AppliedFilters } from '@/components/distiller/types';

import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';
import { useAuth } from '@/hooks/useAuth';
import { useFilters } from '@/hooks/useFilters';
import { useHybridlyTable } from '@/hooks/useHybridlyTable';
import { useTableRowClick } from '@/hooks/useTableRowClick';

import BulkAddStudentGroupStudentDialog from '@/domains/students/components/dialogs/BulkAddStudentGroupStudentDialog.vue';
import BulkCreateStudentGroupDialog from '@/domains/students/components/dialogs/BulkCreateStudentGroupDialog.vue';
import BulkEmptyTrashButton from '@/components/table/BulkEmptyTrashButton.vue';
import BulkRemoveStudentFromStudentGroupDialog from '@/domains/students/components/dialogs/BulkRemoveStudentFromStudentGroupDialog.vue';
import BulkRestoreButton from '@/components/table/BulkRestoreButton.vue';
import BulkTrashButton from '@/components/table/BulkTrashButton.vue';
import Button from '@/components/button/Button.vue';
import EmptyState from '@/components/emptyState/EmptyState.vue';
import IndexPageLayout from '@/components/pageLayout/IndexPageLayout.vue';
import MenuButton from '@/components/button/MenuButton.vue';
import MenuItem from '@/components/menu/MenuItem.vue';
import SendSmsButton from '@/domains/students/components/SendSmsButton.vue';
import StudentActionsMenu from '@/domains/students/components/StudentActionsMenu.vue';
import TableBadgesCell from '@/components/table/TableBadgesCell.vue';
import TableBooleanCell from '@/components/table/TableBooleanCell.vue';
import TableDateCell from '@/components/table/TableDateCell.vue';
import TableEnumCell from '@/components/table/TableEnumCell.vue';
import TabNavDivider from '@/components/tabNav/TabNavDivider.vue';
import TabNavItem from '@/components/tabNav/TabNavItem.vue';
import VerticalDivider from '@/components/verticalDivider/VerticalDivider.vue';

import Building from '@/icons/line/building.svg';
import Check from '@/icons/line/check.svg';
import Clock from '@/icons/line/clock.svg';
import Plus from '@/icons/line/plus.svg';
import Times from '@/icons/line/times.svg';
import UsersAlt from '@/icons/line/users-alt.svg';
import LayerGroup from '@/icons/line/layer-group.svg';

type StudentRow = HybridlyRow<App.Students.Data.StudentData>;

const props = defineProps<{
  hasSmsEnabled: boolean;
  appliedFilters: AppliedFilters;
  archiveActive: boolean;
  counts: {
    active_count: number;
    archived_count: number;
  };
  defaultSchool: App.Configuration.Data.SchoolData;
  distiller: App.Search.Data.DistillerData;
  enrollmentStatuses: EnumData[];
  postSecondaryPlans: EnumData[];
  students: Table<App.Students.Data.StudentData>;
}>();

const { hasPermission } = useAuth();

const { table, tableSelectionState } = useHybridlyTable<App.Students.Data.StudentData, 'cursor'>(
  props,
  'students'
);
const { handleRowClick } = useTableRowClick();

const [isRemoveStudentGroupStudentOpen, toggleRemoveStudentGroupStudentOpen] = useToggle(false);
const [isCreateStudentGroupOpen, toggleCreateStudentGroupOpen] = useToggle(false);
const [isAddStudentGroupStudentOpen, toggleAddStudentGroupStudentOpen] = useToggle(false);
const queryParameters = useQueryParameters();

const { encodeFilters } = useFilters();

const encodedFilters = computed(() => encodeFilters(props.appliedFilters));

const computedSelectedStudentsCount = computed(() =>
  tableSelectionState.value.areAllRecordsSelected
    ? props.counts.active_count
    : tableSelectionState.value.selectedRowIds.length
);

function getFinancialAidStatusIcon(status: string) {
  return {
    none: Times,
    'created-fsa-id': Clock,
    'in-progress': Clock,
    submitted: Clock,
    approved: Check,
    'not-filing': Times
  }[status];
}

const customCells = {
  school: ({ row, column }) => {
    return (
      <TableBadgesCell
        badgesToDisplay={1}
        itemIcon={Building}
        items={[row.value(column.key).name]}
      />
    );
  },
  dob: ({ row, column }) => <TableDateCell date={row.value(column.key)} />,
  status: ({ row, column }) => <TableEnumCell enumData={row.value(column.key)} />,
  last_meeting: ({ row, column }) => <TableDateCell date={row.value(column.key)} />,
  groups: ({ row, column }) => (
    <TableBadgesCell
      badgesToDisplay={2}
      badgeVariant="soft"
      itemIcon={UsersAlt}
      items={row.value(column.key).map((group: App.Students.Data.StudentGroupData) => group.name)}
    />
  ),
  grade: ({ row, column }) => <TableEnumCell enumData={row.value(column.key)} />,
  financial_aid: ({ row, column }) => (
    <TableBadgesCell
      badgesToDisplay={1}
      badgeVariant="soft"
      itemIcon={getFinancialAidStatusIcon(row.value('financial_aid').status.value)}
      items={[row.value(column.key).status.label]}
    />
  ),
  alternative_financial_aid: ({ row }) => (
    <TableBooleanCell boolean={row.value('financial_aid').is_alternative} />
  )
} as CustomCells<
  App.Students.Data.StudentData & {
    alternative_financial_aid: boolean;
  }
>;

function handleStudentRowClick(event: MouseEvent, row: StudentRow) {
  const url = route('students.show', {
    student: row.key
  });
  handleRowClick(url, event);
}
</script>


					<script lang="tsx">
					import __hybridly_layout_0 from '~/resources/layouts/default.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <IndexPageLayout
    title="Students"
    :appliedFilters="appliedFilters"
    :distiller="distiller"
    :table="table"
    :customCells="customCells"
    :stickyEndColumns="1"
    :stickyStartColumns="4"
    :clickableRows="!archiveActive"
    @clickRow="($event, row) => handleStudentRowClick($event, row as StudentRow)"
    :tableSelectionState="tableSelectionState"
  >
    <template #rightButtons>
      <template v-if="tableSelectionState.hasSelection">
        <StudentActionsMenu
          v-if="!archiveActive"
          :tableSelectionState="tableSelectionState"
          :studentCount="computedSelectedStudentsCount"
          :enrollmentStatuses="enrollmentStatuses"
          :postSecondaryPlans="postSecondaryPlans"
          :defaultSchool="defaultSchool"
          :queryParameters="queryParameters"
        />
        <MenuButton
          v-if="!archiveActive"
          buttonColor="slate"
          buttonLabel="Group"
          buttonSize="sm"
          buttonVariant="outlined"
        >
          <MenuItem
            label="Remove From Group"
            description="Remove selected students from an existing group"
            @click="() => toggleRemoveStudentGroupStudentOpen()"
          />
          <MenuItem
            label="Add To Group"
            description="Add selected students to an existing group"
            @click="() => toggleAddStudentGroupStudentOpen()"
          />
          <MenuItem
            label="New Group"
            description="Create a new group from selected students"
            @click="() => toggleCreateStudentGroupOpen()"
          />
        </MenuButton>
        <VerticalDivider v-if="!archiveActive" />
        <BulkTrashButton
          v-if="!archiveActive"
          recordType="Students"
          :route="route('students.bulk-archive')"
          :tableSelectionState="tableSelectionState"
        />
        <BulkRestoreButton
          v-if="archiveActive"
          recordType="Students"
          :route="route('students.bulk-unarchive')"
          :tableSelectionState="tableSelectionState"
        />
        <BulkEmptyTrashButton
          v-if="archiveActive"
          recordType="Students"
          :route="route('students.bulk-delete')"
          :tableSelectionState="tableSelectionState"
        />
        <SendSmsButton v-if="hasSmsEnabled" :tableSelectionState="tableSelectionState" />
      </template>

      <template v-else>
        <Button
          v-if="hasPermission('create-student')"
          :color="ButtonColor.slate"
          :href="route('upload-sessions.draft.index', { uploadType: 'student' })"
          :iconLeft="LayerGroup"
          :size="ButtonSize.sm"
          :variant="ButtonVariant.soft"
        >
          Bulk add
        </Button>
        <Button
          v-if="hasPermission('create-student')"
          :color="ButtonColor.primary"
          :href="route('students.create')"
          :iconLeft="Plus"
          :size="ButtonSize.sm"
          :variant="ButtonVariant.solid"
        >
          Add a student
        </Button>
      </template>
    </template>

    <template #tabs>
      <TabNavItem
        :badgeLabel="counts.active_count"
        :isActive="!archiveActive"
        label="Active"
        variant="inline"
        :href="
          route('students.index', {
            ...(encodedFilters ? { filters: encodedFilters } : {})
          })
        "
      />
      <TabNavDivider layout="inline" />
      <TabNavItem
        :badgeLabel="counts.archived_count ?? 0"
        :isActive="archiveActive"
        label="Trashed"
        variant="inline"
        :href="
          route('students.index', {
            archived: true,
            ...(encodedFilters ? { filters: encodedFilters } : {})
          })
        "
      />
    </template>

    <template #emptyState>
      <EmptyState
        v-if="archiveActive"
        title="No students were moved to the trash"
        description="Trash students will appear on this screen"
      />
      <EmptyState
        v-else
        title="No students found"
        :description="
          hasPermission('create-student') ? 'Create a new student by clicking the button below' : ''
        "
        :actionLabel="hasPermission('create-student') ? 'Add a student' : ''"
        :actionHref="route('students.create')"
      />
    </template>
  </IndexPageLayout>

  <BulkAddStudentGroupStudentDialog
    v-if="isAddStudentGroupStudentOpen"
    :selectedStudentsCount="computedSelectedStudentsCount"
    :tableSelectionState="tableSelectionState"
    isOpen
    @onCancel="() => toggleAddStudentGroupStudentOpen()"
    @onClose="() => toggleAddStudentGroupStudentOpen()"
  />
  <BulkRemoveStudentFromStudentGroupDialog
    v-if="isRemoveStudentGroupStudentOpen"
    :selectedStudentsCount="computedSelectedStudentsCount"
    :tableSelectionState="tableSelectionState"
    isOpen
    @onCancel="() => toggleRemoveStudentGroupStudentOpen()"
    @onClose="() => toggleRemoveStudentGroupStudentOpen()"
  />
  <BulkCreateStudentGroupDialog
    v-if="isCreateStudentGroupOpen"
    :defaultSchool="defaultSchool"
    :selectedStudentsCount="computedSelectedStudentsCount"
    :tableSelectionState="tableSelectionState"
    isOpen
    @onCancel="() => toggleCreateStudentGroupOpen()"
    @onClose="() => toggleCreateStudentGroupOpen()"
  />
</template>
